import { DropdownMenu as Radix, Tooltip as RadixTooltip, Tabs as RadixTabs } from 'radix-ui'
import type { ReactNode } from 'react'

import { cn } from '../cn'

/* ---------- Dropdown menu ---------- */

export interface MenuItem {
  readonly id: string
  readonly label: ReactNode
  readonly icon?: ReactNode
  readonly onSelect?: () => void
  readonly disabled?: boolean
  readonly danger?: boolean
  /** Insert a separator before this item. */
  readonly separatorBefore?: boolean
}

export interface DropdownMenuProps {
  readonly trigger: ReactNode
  readonly items: readonly MenuItem[]
  readonly label?: string
  readonly align?: 'start' | 'end'
}

export function DropdownMenu({ trigger, items, label, align = 'end' }: DropdownMenuProps) {
  return (
    <Radix.Root>
      <Radix.Trigger asChild>{trigger}</Radix.Trigger>
      <Radix.Portal>
        <Radix.Content
          align={align}
          sideOffset={6}
          aria-label={label}
          className="z-50 min-w-[200px] rounded-md border border-line bg-panel p-1 shadow-lg data-[state=open]:animate-[fade-in_.12s_ease]"
        >
          {items.map((item) => (
            <div key={item.id}>
              {item.separatorBefore && <Radix.Separator className="my-1 h-px bg-line" />}
              <Radix.Item
                disabled={item.disabled}
                onSelect={item.onSelect}
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded-sm px-2.5 py-2 text-[13px] outline-none select-none data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50 data-[highlighted]:bg-paper [&>svg]:size-4',
                  item.danger ? 'text-lost' : 'text-ink',
                )}
              >
                {item.icon}
                {item.label}
              </Radix.Item>
            </div>
          ))}
        </Radix.Content>
      </Radix.Portal>
    </Radix.Root>
  )
}

/* ---------- Tooltip ---------- */

export function TooltipProvider({ children }: { readonly children: ReactNode }) {
  return <RadixTooltip.Provider delayDuration={300}>{children}</RadixTooltip.Provider>
}

export function Tooltip({
  content,
  children,
  side = 'top',
}: {
  readonly content: ReactNode
  readonly children: ReactNode
  readonly side?: 'top' | 'right' | 'bottom' | 'left'
}) {
  return (
    <RadixTooltip.Root>
      <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
      <RadixTooltip.Portal>
        <RadixTooltip.Content
          side={side}
          sideOffset={6}
          className="z-50 rounded-sm bg-ink px-2.5 py-1.5 text-[12px] text-white shadow-lg"
        >
          {content}
          <RadixTooltip.Arrow className="fill-ink" />
        </RadixTooltip.Content>
      </RadixTooltip.Portal>
    </RadixTooltip.Root>
  )
}

/* ---------- Tabs ---------- */

export interface TabItem {
  readonly value: string
  readonly label: ReactNode
  readonly content: ReactNode
  readonly disabled?: boolean
}

export interface TabsProps {
  readonly items: readonly TabItem[]
  readonly value?: string
  readonly defaultValue?: string
  readonly onValueChange?: (value: string) => void
  readonly className?: string
}

export function Tabs({ items, value, defaultValue, onValueChange, className }: TabsProps) {
  return (
    <RadixTabs.Root
      value={value}
      defaultValue={defaultValue ?? items[0]?.value}
      onValueChange={onValueChange}
      className={className}
    >
      <RadixTabs.List className="flex gap-1 overflow-x-auto border-b border-line">
        {items.map((item) => (
          <RadixTabs.Trigger
            key={item.value}
            value={item.value}
            disabled={item.disabled}
            className="-mb-px border-b-2 border-transparent px-3 py-2.5 text-[13px] font-semibold whitespace-nowrap text-muted transition-colors hover:text-ink data-[state=active]:border-brand data-[state=active]:text-ink disabled:opacity-50"
          >
            {item.label}
          </RadixTabs.Trigger>
        ))}
      </RadixTabs.List>
      {items.map((item) => (
        <RadixTabs.Content key={item.value} value={item.value} className="pt-4 outline-none">
          {item.content}
        </RadixTabs.Content>
      ))}
    </RadixTabs.Root>
  )
}
